"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, ArrowLeft, Send, CheckCircle2, Loader2 } from "lucide-react";
import Link from "next/link";
import { api } from "@/lib/api";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.submitContactForm(form);
      setSubmitted(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-mesh pb-24 pt-32">
      <div className="mx-auto max-w-5xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest mb-6 border border-emerald-100">
            <Mail size={12} />
            Get In Touch
          </div>
          <h1 className="text-5xl font-black tracking-tight text-slate-950 mb-6 italic">
            Contact Us
          </h1>
          <p className="text-slate-500 font-medium max-w-xl mx-auto">
            Questions about a job, a bid, or your account? Send us a message and we'll get back to you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-8">
          {/* Contact details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="bg-white border border-slate-200 rounded-[2rem] p-8 shadow-sm">
              <div className="h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                <Mail size={20} />
              </div>
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-2">Email</h3>
              <a href="mailto:info@fixerlocal.com" className="text-lg font-bold text-slate-950 hover:text-emerald-600 transition-colors">
                info@fixerlocal.com
              </a>
            </div>

            <div className="bg-white border border-slate-200 rounded-[2rem] p-8 shadow-sm">
              <div className="h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                <MapPin size={20} />
              </div>
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-2">Location</h3>
              <p className="text-lg font-bold text-slate-950">Sydney, NSW, Australia</p>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white border border-slate-200 rounded-[2rem] p-8 shadow-sm"
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="h-16 w-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="text-xl font-black text-slate-950 mb-2">Message sent</h3>
                <p className="text-slate-500 font-medium max-w-sm mb-6">
                  Thanks for reaching out - we'll get back to you at the email you provided as soon as we can.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-3 rounded-xl bg-slate-950 text-white text-sm font-bold hover:bg-slate-800 transition-all"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Name</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full h-14 rounded-2xl bg-slate-50 border border-slate-200 px-5 text-sm font-semibold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Email</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full h-14 rounded-2xl bg-slate-50 border border-slate-200 px-5 text-sm font-semibold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Subject</label>
                  <input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="w-full h-14 rounded-2xl bg-slate-50 border border-slate-200 px-5 text-sm font-semibold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all"
                    placeholder="What's this about?"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Message</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full h-40 rounded-2xl bg-slate-50 border border-slate-200 p-5 text-sm font-semibold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>
                {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-100 disabled:opacity-60 active:scale-[0.98]"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  Send Message
                </button>
              </form>
            )}
          </motion.div>
        </div>

        <div className="mt-12 text-center">
          <Link href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-600 text-sm font-bold transition-colors">
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </div>
    </main>
  );
}
